import { useState, lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";
import { trackPageView } from "./utils/analytics";
import { captureAttribution } from "./lib/attribution";

// Layout (always loaded)
import Header from "./components/Header";
import Footer from "./components/Footer";
import { GeoSuggestChip } from "./components/GeoSuggestChip";
import { AirportEsimBanner } from "./components/AirportEsimBanner";

// Home loads eagerly (critical path)
import HomePage from "./pages/HomePage";

// Lazy-loaded pages
const FibraMovilPage = lazy(() => import("./pages/FibraMovilPage"));
const TarifasMovilPage = lazy(() => import("./pages/TarifasMovilPage"));
const FibraFijoPage = lazy(() => import("./pages/FibraFijoPage"));
const EmpresasPage = lazy(() => import("./pages/EmpresasPage"));
const TVPage = lazy(() => import("./pages/TVPage"));
const InternetRuralPage = lazy(() => import("./pages/InternetRuralPage"));
const RoamingPage = lazy(() => import("./pages/RoamingPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const EstudioZonaPage = lazy(() => import("./pages/EstudioZonaPage"));
const TravelPage = lazy(() => import("./pages/TravelPage"));
const TravelLaunchPage = lazy(() => import("./pages/TravelLaunchPage"));
const TravelDestinationPage = lazy(() => import("./pages/TravelDestinationPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const SpeedTestPage = lazy(() => import("./pages/SpeedTestPage"));
const DistributorLoginPage = lazy(() => import("./pages/DistributorLoginPage"));
const DistributorDashboardPage = lazy(() => import("./pages/DistributorDashboardPage"));
const RoadmapPage = lazy(() => import("./pages/RoadmapPage"));
const FibraLocalPage = lazy(() => import("./pages/FibraLocalPage"));
const FibraIslaPage = lazy(() => import("./pages/FibraIslaPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const CoverageChecklistPage = lazy(() => import("./pages/CoverageChecklistPage"));
const PresupuestoPublicPage = lazy(() => import("./pages/PresupuestoPublicPage"));
const AgenciasViajePage = lazy(() => import("./pages/AgenciasViajePage"));
const PeninsulaPage = lazy(() => import("./pages/PeninsulaPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

const TRAVEL_ENABLED = process.env.REACT_APP_ENABLE_TRAVEL === "true";

// Modals (always loaded – triggered from any page)
const CoverageChecker = lazy(() => import("./components/CoverageChecker"));
const ContractModal = lazy(() => import("./components/ContractModal"));
const LegalPageModal = lazy(() => import("./components/LegalPageModal"));
import CookieBanner from "./components/CookieBanner";
import FloatingWhatsApp from "./components/FloatingWhatsApp";
import AIChatWidget from "./components/AIChatWidget";
import PromotionsBanner from "./components/PromotionsBanner";
import { DistributorAuthProvider } from "./contexts/DistributorAuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";

// Page view tracker + hash scroll
const PageTracker = () => {
  const location = useLocation();
  useEffect(() => { trackPageView(location.pathname); }, [location.pathname]);
  useEffect(() => { captureAttribution(location.search, location.pathname); }, [location.search, location.pathname]);
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const timer = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [location.hash, location.pathname]);
  return null;
};

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center bg-[#0A0A0A]">
    <div className="w-8 h-8 border-2 border-[#7DD500]/30 border-t-[#7DD500] rounded-full animate-spin" />
  </div>
);

const AppContent = () => {
  const [showCoverageChecker, setShowCoverageChecker] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [prefilledCoverage, setPrefilledCoverage] = useState(null);
  const [legalPage, setLegalPage] = useState(null);
  const [modalsMounted, setModalsMounted] = useState({ coverage: false, contract: false });
  const location = useLocation();

  const openCoverage = () => { setModalsMounted(m => ({ ...m, coverage: true })); setShowCoverageChecker(true); };
  // openContract called without args opens fresh; with payload pre-fills address+coverage
  const openContract = (prefill = null) => {
    setModalsMounted(m => ({ ...m, contract: true }));
    setPrefilledCoverage(prefill);
    setShowContractModal(true);
  };
  const closeContract = () => {
    setShowContractModal(false);
    // Clear prefill after a tick so modal close animation completes
    setTimeout(() => setPrefilledCoverage(null), 200);
  };

  // Enlace "Continuar con la contratación" desde /p/{token}: abre el contrato con la opción elegida precargada
  useEffect(() => {
    const qs = new URLSearchParams(location.search);
    const token = qs.get("presupuesto");
    if (!token) return;
    const optionId = qs.get("opcion");
    import("./utils/presupuestoPrefill").then(({ buildContractPrefill }) =>
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/p/${token}`).then(r => (r.ok ? r.json() : null)).then(data => {
        if (!data) return;
        const prefill = buildContractPrefill(data, optionId || data.chosen_option?.id);
        if (prefill) openContract(prefill);
        window.history.replaceState({}, "", location.pathname);
      }).catch(() => {})
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const isAdmin = location.pathname === "/admin";

  // SEO: una sola URL por página → sin barra final y en minúsculas (evita "Duplicada sin canónica" en Search Console)
  const normalizedPath = location.pathname.length > 1 ? location.pathname.replace(/\/+$/, "").toLowerCase() : location.pathname;
  if (normalizedPath !== location.pathname) {
    return <Navigate to={{ pathname: normalizedPath, search: location.search, hash: location.hash }} replace />;
  }

  if (isAdmin) {
    return (
      <Suspense fallback={<PageLoader />}>
        <PageTracker />
        <AdminDashboard />
      </Suspense>
    );
  }

  return (
    <div className="App">
      <PageTracker />
      <Header onCheckCoverage={openCoverage} onContract={openContract} />

      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<HomePage onCheckCoverage={openCoverage} onContract={openContract} />} />
          <Route path="/fibra-movil" element={<FibraMovilPage onContract={openContract} />} />
          <Route path="/peninsula" element={<PeninsulaPage onContract={openContract} />} />
          <Route path="/tarifas-movil" element={<TarifasMovilPage onContract={openContract} />} />
          <Route path="/fibra-fijo" element={<FibraFijoPage onContract={openContract} />} />
          <Route path="/fibra-canarias/:slug" element={<FibraLocalPage onCheckCoverage={openCoverage} onContract={openContract} />} />
          {/* Island-level SEO landings */}
          <Route path="/fibra-gran-canaria" element={<FibraIslaPage onCheckCoverage={openCoverage} onContract={openContract} />} />
          <Route path="/fibra-tenerife" element={<FibraIslaPage onCheckCoverage={openCoverage} onContract={openContract} />} />
          <Route path="/fibra-lanzarote" element={<FibraIslaPage onCheckCoverage={openCoverage} onContract={openContract} />} />
          <Route path="/fibra-fuerteventura" element={<FibraIslaPage onCheckCoverage={openCoverage} onContract={openContract} />} />
          <Route path="/fibra-la-palma" element={<FibraIslaPage onCheckCoverage={openCoverage} onContract={openContract} />} />
          <Route path="/fibra-la-gomera" element={<FibraIslaPage onCheckCoverage={openCoverage} onContract={openContract} />} />
          <Route path="/fibra-el-hierro" element={<FibraIslaPage onCheckCoverage={openCoverage} onContract={openContract} />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/empresas" element={<EmpresasPage onContract={openContract} />} />
          <Route path="/tv" element={<TVPage />} />
          <Route path="/internet-rural" element={<InternetRuralPage />} />
          <Route path="/roaming" element={<RoamingPage />} />
          <Route path="/contacto" element={<ContactPage />} />
          <Route path="/estudio-zona" element={<EstudioZonaPage />} />
          <Route path="/checklist-cobertura" element={<CoverageChecklistPage />} />
          <Route path="/p/:token" element={<PresupuestoPublicPage />} />
          <Route path="/test-velocidad" element={<SpeedTestPage />} />
          <Route path="/distribuidor" element={<DistributorLoginPage />} />
          <Route path="/distribuidor/dashboard" element={<DistributorDashboardPage />} />
          {/* Public roadmap teaser (Travel coming soon) */}
          <Route path="/roadmap" element={<RoadmapPage />} />
          <Route path="/proximamente" element={<RoadmapPage />} />
          {/* Travel routes (toggleable via REACT_APP_ENABLE_TRAVEL) */}
          {TRAVEL_ENABLED && (
            <>
              <Route path="/travel" element={<TravelPage />} />
              <Route path="/travel/payment-ok" element={<TravelPage resultType="ok" />} />
              <Route path="/travel/payment-ko" element={<TravelPage resultType="ko" />} />
              {/* Destination landings — slug routed via TravelDestinationPage */}
              <Route path="/travel/:slug" element={<TravelDestinationPage />} />
              {/* Programa Partner agencias de viajes - landing dedicada */}
              <Route path="/agencias" element={<AgenciasViajePage />} />
              <Route path="/agencias-viajes" element={<AgenciasViajePage />} />
              <Route path="/lanzamiento-travel" element={<TravelLaunchPage />} />
              <Route path="/travel-promo" element={<TravelLaunchPage />} />
            </>
          )}
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/legal/:slug" element={<LegalPage />} />
          <Route path="/privacidad" element={<LegalPage />} />
          <Route path="/cookies" element={<LegalPage />} />
          <Route path="/condiciones-contratacion" element={<LegalPage />} />
          {TRAVEL_ENABLED && <Route path="/condiciones-esim" element={<LegalPage />} />}
          <Route path="/proteccion-datos" element={<LegalPage />} />
          <Route path="/velocidades" element={<LegalPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>

      <Footer onLegalPage={(slug) => setLegalPage(slug)} />
      <GeoSuggestChip />
      <AirportEsimBanner />

      <Suspense fallback={null}>
        {modalsMounted.coverage && (
          <CoverageChecker
            isVisible={showCoverageChecker}
            onClose={() => setShowCoverageChecker(false)}
            onContract={openContract}
            zona={location.pathname === "/peninsula" ? "peninsula" : null}
          />
        )}
        {modalsMounted.contract && (
          <ContractModal
            isVisible={showContractModal}
            onClose={closeContract}
            prefilledCoverage={prefilledCoverage}
          />
        )}
        {legalPage && (
          <LegalPageModal
            isVisible={!!legalPage}
            onClose={() => setLegalPage(null)}
            pageSlug={legalPage}
          />
        )}
      </Suspense>

      <CookieBanner />

      <PromotionsBanner />

      <FloatingWhatsApp />
      <AIChatWidget />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <LanguageProvider>
          <DistributorAuthProvider>
            <AppContent />
          </DistributorAuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
